import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import AdminPanel from './AdminPanel';
import './style1.css';

interface ViewProps {
    username: string | null;
    onLogout?: () => void;
}

const navLinks = [
    { name: 'HOME', href: '/' },
    { name: 'PROGRAMS', href: '/programs' },
    { name: 'VIDEOS', href: '/videos' },
    { name: 'IMAGES', href: '/images' },
    { name: 'DEMOS', href: '/demos' },
    { name: 'CERTIFICATES', href: '/certificates' },
    { name: 'ABOUT', href: '/about' },
];

const View: React.FC<ViewProps> = ({ username, onLogout }) => {
    const [data, setData] = useState<any[]>([]);
    const [type, setType] = useState<string>('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    if (username !== 'admin') {
        return <div style={{ textAlign: 'center', marginTop: '2rem' }}>Access denied.</div>;
    }

    const handleFetch = async (endpoint: string, label: string) => {
        setError('');
        setLoading(true);
        setType(label);
        setData([]);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(endpoint, {
                headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            });
            if (response.ok) {
                const result = await response.json();
                setData(result);
            } else {
                setError('Failed to fetch data.');
            }
        } catch {
            setError('Network error.');
        }
        setLoading(false);
    };

    // Helper to render table headers and rows dynamically
    const renderTable = () => {
        if (!data || data.length === 0) return null;
        const headers = Object.keys(data[0]);
        return (
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 20 }}>
                <thead>
                    <tr>
                        {headers.map(h => (
                            <th key={h} style={{ border: '1px solid #ccc', padding: 8, background: '#f0f0f0' }}>{h}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {data.map((row, idx) => (
                        <tr key={idx}>
                            {headers.map(h => (
                                <td key={h} style={{ border: '1px solid #ccc', padding: 8 }}>{row[h]}</td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    };

    return (
        <div className="modern-layout" style={{ position: 'relative' }}>
            <header className="modern-header">
                <div className="header-container">
                    <nav className="modern-nav">
                        {navLinks.map(link =>
                            link.href.startsWith('/')
                                ? <Link
                                    key={link.name}
                                    to={link.href}
                                    className="modern-nav-link"
                                    style={{ textDecoration: 'none' }}
                                  >
                                    {link.name}
                                  </Link>
                                : <a
                                    key={link.name}
                                    href={link.href}
                                    className="modern-nav-link"
                                  >
                                    {link.name}
                                  </a>
                        )}
                        {onLogout && (
                            <button className="logout-btn" onClick={onLogout}>LOG OUT</button>
                        )}
                    </nav>
                </div>
            </header>
            <div
                style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    zIndex: 10,
                    pointerEvents: 'none'
                }}
            >
                <div style={{ pointerEvents: 'auto' }}>
                    <AdminPanel />
                </div>
            </div>
            <main className="modern-content">
                <div
                    style={{
                        maxWidth: 700,
                        margin: '2rem auto',
                        background: '#fff',
                        borderRadius: '16px',
                        boxShadow: '0 2px 16px #6a11cb22',
                        padding: '2rem',
                        color: '#222',
                        fontSize: '1.1rem'
                    }}
                >
                    <h2 style={{ textAlign: 'center', marginBottom: '1.5rem' }}>View Data</h2>
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center', marginBottom: 24 }}>
                        <button
                            onClick={() => handleFetch('/allusers', 'Users')}
                            style={{
                                padding: '0.5rem 1.5rem',
                                borderRadius: 6,
                                border: 'none',
                                background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
                                color: '#fff',
                                fontWeight: 'bold',
                                cursor: 'pointer'
                            }}
                        >
                            List Users
                        </button>
                        <button
                            onClick={() => handleFetch('/allprograms', 'Programs')}
                            style={{
                                padding: '0.5rem 1.5rem',
                                borderRadius: 6,
                                border: 'none',
                                background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
                                color: '#fff',
                                fontWeight: 'bold',
                                cursor: 'pointer'
                            }}
                        >
                            List Programs
                        </button>
                        <button
                            onClick={() => handleFetch('/allvideos', 'Videos')}
                            style={{
                                padding: '0.5rem 1.5rem',
                                borderRadius: 6,
                                border: 'none',
                                background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
                                color: '#fff',
                                fontWeight: 'bold',
                                cursor: 'pointer'
                            }}
                        >
                            List Videos
                        </button>
                        <button
                            onClick={() => handleFetch('/allcertificates', 'Certificates')}
                            style={{
                                padding: '0.5rem 1.5rem',
                                borderRadius: 6,
                                border: 'none',
                                background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
                                color: '#fff',
                                fontWeight: 'bold',
                                cursor: 'pointer'
                            }}
                        >
                            List Certificates
                        </button>
                        <button
                            onClick={() => handleFetch('/allimages', 'Images')}
                            style={{
                                padding: '0.5rem 1.5rem',
                                borderRadius: 6,
                                border: 'none',
                                background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
                                color: '#fff',
                                fontWeight: 'bold',
                                cursor: 'pointer'
                            }}
                        >
                            List Images
                        </button>
                        <button
                            onClick={() => handleFetch('/alldemos', 'Demos')}
                            style={{
                                padding: '0.5rem 1.5rem',
                                borderRadius: 6,
                                border: 'none',
                                background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
                                color: '#fff',
                                fontWeight: 'bold',
                                cursor: 'pointer'
                            }}
                        >
                            List Demos
                        </button>
                    </div>
                    {loading && <div style={{ textAlign: 'center', margin: '1rem 0' }}>Loading...</div>}
                    {error && <div style={{ color: 'red', margin: '1rem 0', textAlign: 'center' }}>{error}</div>}
                    {type && data.length > 0 && (
                        <div>
                            <h3 style={{ textAlign: 'center', margin: '1rem 0' }}>{type}</h3>
                            <div style={{ overflowX: 'auto' }}>
                                {renderTable()}
                            </div>
                        </div>
                    )}
                    {type && !loading && data.length === 0 && !error && (
                        <div style={{ textAlign: 'center', margin: '1rem 0' }}>No {type.toLowerCase()} found.</div>
                    )}
                </div>
            </main>
            <footer className="modern-footer">
                &copy; {new Date().getFullYear()} Timo
            </footer>
        </div>
    );
};

export default View;